/**
 * Bucket + bucket-group mutation hooks (FE-3, PHASE1 §4 bucket-groups + buckets,
 * §7.3 invalidation). Every mutation invalidates the single `['buckets']` cache
 * (the group→bucket tree everything reads from).
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  BucketCreate,
  BucketGroupCreate,
  BucketGroupOut,
  BucketGroupUpdate,
  BucketOut,
  BucketUpdate,
} from '../types.ts';

// ── Bucket groups ───────────────────────────────────────────────────────────

/** POST /api/bucket-groups — create a group. */
export function useCreateBucketGroup(): UseMutationResult<BucketGroupOut, Error, BucketGroupCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BucketGroupCreate) => api.post<BucketGroupOut>('/api/bucket-groups', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.buckets });
    },
  });
}

export interface BucketGroupUpdateVars {
  id: number;
  body: BucketGroupUpdate;
}

/** PATCH /api/bucket-groups/{id} — rename / recolor / archive a group. */
export function useUpdateBucketGroup(): UseMutationResult<
  BucketGroupOut,
  Error,
  BucketGroupUpdateVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: BucketGroupUpdateVars) =>
      api.patch<BucketGroupOut>(`/api/bucket-groups/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.buckets });
    },
  });
}

/** POST /api/bucket-groups/reorder — position in `ordered_ids` becomes `sort_order`. */
export function useReorderBucketGroups(): UseMutationResult<BucketGroupOut[], Error, number[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      api.post<BucketGroupOut[]>('/api/bucket-groups/reorder', { ordered_ids: orderedIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.buckets });
    },
  });
}

// ── Buckets ─────────────────────────────────────────────────────────────────

/** POST /api/buckets — create a bucket in a group. */
export function useCreateBucket(): UseMutationResult<BucketOut, Error, BucketCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BucketCreate) => api.post<BucketOut>('/api/buckets', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.buckets });
    },
  });
}

export interface BucketUpdateVars {
  id: number;
  body: BucketUpdate;
}

/** PATCH /api/buckets/{id} — rename / recolor / move / archive a bucket. */
export function useUpdateBucket(): UseMutationResult<BucketOut, Error, BucketUpdateVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: BucketUpdateVars) =>
      api.patch<BucketOut>(`/api/buckets/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.buckets });
    },
  });
}

export interface BucketReorderVars {
  groupId: number;
  orderedIds: number[];
}

/** POST /api/buckets/reorder — reorder within one group. */
export function useReorderBuckets(): UseMutationResult<BucketOut[], Error, BucketReorderVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, orderedIds }: BucketReorderVars) =>
      api.post<BucketOut[]>('/api/buckets/reorder', {
        group_id: groupId,
        ordered_ids: orderedIds,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.buckets });
    },
  });
}
